/**
 * NamedPassesController — renders sectional-labelled VFR mountain
 * passes as a togglable map layer.
 *
 * Boss request 2026-05-19 07:38 PT — pairs with the terrain-clearance
 * auto-router (/api/route/pass-finder) so pilots can see the same
 * named passes the router biases toward.
 *
 * Data source: /api/passes (static corpus in api/data/namedPasses.ts).
 * Fetched once per session (passes don't change), no bbox filtering —
 * the corpus is small (<200 entries).
 *
 * Rendering:
 *   - Cyan triangle marker (pointing up — "saddle" iconography)
 *   - Pass name + elevation as a labelled symbol at z >= 8
 *   - Click → popup with elev, region, notes
 *
 * Effect-only component — returns null.
 */
import { useEffect, useRef } from 'react';
import maplibre, { type GeoJSONSource, type Map as MapInstance } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { getMapInstance, safeGetSource, EMPTY_FC } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';
import type { NamedPass } from '../../data/namedPasses';

const SOURCE_ID = 'named-passes';

// Module-scoped cache — passes corpus is static, fetch once per app load.
let cached: NamedPass[] | null = null;
let inFlight: Promise<NamedPass[]> | null = null;

async function fetchPasses(): Promise<NamedPass[]> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const r = await fetch('/api/passes');
      if (!r.ok) return [];
      const j = (await r.json()) as { ok?: boolean; passes?: NamedPass[] };
      cached = j.passes ?? [];
      return cached;
    } catch {
      return [];
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

function passesToFC(passes: NamedPass[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: passes.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      properties: {
        ident: p.ident,
        name: p.name,
        elevFt: p.elevFt,
        region: p.region,
        notes: p.notes ?? '',
        label: `${p.name} · ${p.elevFt.toLocaleString()}'`,
      },
    })),
  };
}

// HI-1 step 3 (2026-05-23): rebuildRef prop removed. The controller now
// registers its style-rebuild restore with layerVisibilityRegistry.
export function NamedPassesController() {
  const enabled = useLayerVisibilityStore((s) => s.layers.namedPasses);
  const dataRef = useRef<GeoJSON.FeatureCollection>(EMPTY_FC);

  const pushData = (map: MapInstance | null): void => {
    if (!map) return;
    const src = safeGetSource<GeoJSONSource>(map, SOURCE_ID);
    if (!src) return;
    src.setData(enabled ? dataRef.current : EMPTY_FC);
  };

  const install = (map: MapInstance): void => {
    if (!map.getSource(SOURCE_ID)) {
      try {
        map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
      } catch { /* race */ }
    }
    // Pass marker — cyan triangle (pointing up: saddle iconography).
    if (!map.getLayer('passes-marker')) {
      try {
        map.addLayer({
          id: 'passes-marker',
          type: 'symbol',
          source: SOURCE_ID,
          layout: {
            'text-field': '▲',
            'text-font': ['Noto Sans Regular'],
            'text-size': 18,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-anchor': 'center',
          },
          paint: {
            'text-color': '#5fc7d4',
            'text-halo-color': '#04212a',
            'text-halo-width': 1.5,
          },
        });
      } catch { /* race */ }
    }
    // Pass label — name + elevation.
    if (!map.getLayer('passes-label')) {
      try {
        map.addLayer({
          id: 'passes-label',
          type: 'symbol',
          source: SOURCE_ID,
          minzoom: 7.5,
          layout: {
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': [
              'interpolate', ['linear'], ['zoom'],
              7.5, 10,
              10, 12,
              13, 13,
            ],
            'text-anchor': 'top',
            'text-offset': [0, 0.8],
            'text-allow-overlap': false,
            'text-optional': true,
          },
          paint: {
            'text-color': '#9beaf3',
            'text-halo-color': '#04212a',
            'text-halo-width': 1.5,
          },
        });
      } catch { /* race */ }
    }
  };

  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    install(map);

    // Click handler → flyTo + a transient popup-like console hint.
    // (Full popup UX deferred — boss said "highlight them somehow".)
    let popupCleanup: (() => void) | null = null;
    const onClick = (e: any): void => {
      const feats = e?.features;
      if (!feats?.length) return;
      const p = feats[0].properties ?? {};
      const html = `
        <div style="font:12px/1.4 system-ui,sans-serif;color:#d0eef3;">
          <div style="font-weight:600;color:#9beaf3;">${p.name ?? p.ident ?? 'Pass'}</div>
          <div>${Number(p.elevFt).toLocaleString()}' MSL · ${p.region}</div>
          ${p.notes ? `<div style="margin-top:4px;opacity:0.85;">${p.notes}</div>` : ''}
        </div>`;
      if (popupCleanup) popupCleanup();
      const popup = new maplibre.Popup({ closeButton: true, offset: 14 })
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(map);
      popupCleanup = () => { try { popup.remove(); } catch { /* noop */ } };
    };
    map.on('click', 'passes-marker', onClick);
    map.on('click', 'passes-label', onClick);
    // Cursor feedback
    const onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const onLeave = () => { map.getCanvas().style.cursor = ''; };
    map.on('mouseenter', 'passes-marker', onEnter);
    map.on('mouseleave', 'passes-marker', onLeave);

    // Kick off the fetch — only fetch when the layer becomes enabled
    // the first time. The data is small so it's fine to fetch eagerly
    // either way; we just gate the push on `enabled`.
    void fetchPasses().then((passes) => {
      dataRef.current = passesToFC(passes);
      pushData(getMapInstance());
    });

    // HI-1 step 2/3: style-rebuild restore registered with the central
    // registry. MapView's styledata + map.on('load') handlers call us
    // via runStyleRebuildHandlers — no prop-drilled ref.
    const restore = (): void => {
      const m = getMapInstance();
      if (!m) return;
      install(m);
      pushData(m);
    };
    const unregister = registerStyleRebuild({
      id: 'namedPasses',
      restore,
    });

    return () => {
      if (popupCleanup) popupCleanup();
      try { map.off('click', 'passes-marker', onClick); } catch { /* noop */ }
      try { map.off('click', 'passes-label', onClick); } catch { /* noop */ }
      try { map.off('mouseenter', 'passes-marker', onEnter); } catch { /* noop */ }
      try { map.off('mouseleave', 'passes-marker', onLeave); } catch { /* noop */ }
      unregister();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to toggle.
  useEffect(() => {
    pushData(getMapInstance());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return null;
}
